import Component from '@glimmer/component';
import { service } from '@ember/service';
import { get } from '@ember/helper';
import { t } from 'ember-intl';
import { or } from 'ember-truth-helpers';
import {
  HdsPageHeader,
  HdsBadge,
  HdsLinkStandalone,
  HdsIcon,
} from '@hashicorp/design-system-components/components';
import {
  CoreLayoutMain,
  BreadcrumbsContainer,
  FlexGrid,
  DefinitionList,
  Flex,
} from 'core/components';
import { boxPadding } from 'core/modifiers';
import { optionForSourceState } from 'core/helpers';

/**
 *
 * `PageAccountSettingsBasicInfo` renders the user's profile.
 *
 *
 * ```
 * <Page::AccountSettings::BasicInfo />
 * ```
 *
 * @class PageAccountSettingsBasicInfo
 *
 */
export default class PageAccountSettingsBasicInfo extends Component {
  @service currentUser;

  <template>
    <CoreLayoutMain>
      <:header>
        <HdsPageHeader as |PH|>
          <PH.Breadcrumb>
            <BreadcrumbsContainer />
          </PH.Breadcrumb>
          <PH.Title>
            {{t "components.page.account-settings.nav.info"}}
          </PH.Title>
          <PH.IconTile @icon="user" />
        </HdsPageHeader>
      </:header>
      <:body>
        <div class="accountSettings">
          {{#let this.currentUser.user as |user|}}
            <FlexGrid class="accountSettings__flexContainer" as |FG|>
              <FG.Item @xs="12" @md="8" @lg="6">
                <DefinitionList {{boxPadding "md 0 xl 0"}} as |DL|>
                  <DL.Key>
                    {{t "components.page.account-settings.user.name"}}
                  </DL.Key>
                  <DL.Value>
                    {{user.fullName}}
                  </DL.Value>

                  <DL.Key>
                    {{t "components.page.account-settings.user.email"}}
                  </DL.Key>
                  <DL.Value>
                    {{user.email}}
                  </DL.Value>

                  <DL.Key>
                    {{t "components.page.account-settings.user.signIn"}}
                  </DL.Key>
                  <DL.Value data-test-sign-in-method>
                    <Flex @direction="row" @gap="xs">
                      {{#each user.identityTypes as |_ index|}}
                        {{#let
                          (optionForSourceState
                            "identity_types" (get user.identityTypes index)
                          )
                          as |option|
                        }}
                          <HdsBadge
                            @text={{t option.text}}
                            @color={{option.color}}
                            @icon={{option.icon}}
                            @isIconOnly={{option.isIconOnly}}
                            @size={{option.size}}
                            @type={{option.type}}
                          />
                        {{/let}}
                      {{/each}}
                    </Flex>
                  </DL.Value>

                  <DL.Key>
                    {{t
                      "components.page.account-settings.user.email-preferences"
                    }}
                  </DL.Key>
                  <DL.Value>
                    <HdsLinkStandalone
                      @icon="arrow-right"
                      @iconPosition="trailing"
                      @text={{t
                        "components.page.account-settings.user.email-preferences-link"
                      }}
                      @href="https://go.hashicorp.com/manage-your-email-preferences.html"
                    />
                  </DL.Value>
                  <DL.Key>
                    {{t "components.page.account-settings.user.organizations"}}
                  </DL.Key>
                  <DL.Value>
                    <HdsLinkStandalone
                      @icon="arrow-right"
                      @iconPosition="trailing"
                      @text={{t
                        "components.page.account-settings.user.orgs"
                        numberOfOrganizations=this.currentUser.organizations.length
                        htmlSafe=true
                      }}
                      @route="cloud.orgs.list"
                      data-test-user-organizations
                    />
                  </DL.Value>
                </DefinitionList>
              </FG.Item>
              <FG.Item @xs="12" @md="4" @lg="6">
                <div
                  class="accountSettings__avatar"
                  aria-hidden="true"
                  data-test-avatar
                >
                  {{#if user.picture}}
                    <img
                      src={{user.picture}}
                      alt={{or @user.name "user avatar"}}
                      data-test-avatar-img
                    />
                  {{else}}
                    <HdsIcon
                      @name="user"
                      @size="24"
                      @stretched={{true}}
                      @isInline={{true}}
                      aria-label={{or @user.name "user avatar"}}
                      data-test-avatar-icon
                    />
                  {{/if}}
                </div>
              </FG.Item>
            </FlexGrid>
          {{/let}}
        </div>
      </:body>
    </CoreLayoutMain>
  </template>
}
